import { DisplacementFilter } from '@/pixi/filters/DisplacementFilter'
import { ChromaShiftFilter } from '@/pixi/filters/ChromaShiftFilter'
import { OldTVFilter } from '@/pixi/filters/OldTVFilter'
import { DuotoneFilter } from '@/pixi/filters/DuotoneFilter'
import { PixelateFilter } from '@/pixi/filters/PixelateFilter'
import { RadialBlurFilter } from '@/pixi/filters/blur/RadialBlurFilter'
import { ZoomBlurFilter } from '@/pixi/filters/blur/ZoomBlurFilter'
import { MotionBlurFilter } from '@/pixi/filters/blur/MotionBlurFilter'
import { fadedGradientStops, vintageGradientStops, contrastGradientStops, sepiaGradientStops, coolGradientStops } from '@/static/gradient-maps'
import { NeonFilter } from '@/pixi/filters/NeonFilter'
import { NeonBlendFilter } from '@/pixi/filters/NeonBlendFilter'
import { BloomFilter } from '@/pixi/filters/BloomFilter'
import { VintageFilter } from '@/pixi/filters/VintageFilter'
import { MirrorFilter } from '@/pixi/filters/mirror/MirrorFilter'
import { KaleidoscopeFilter } from '@/pixi/filters/mirror/KaleidoscopeFilter'
import { VHSFilter } from '@/pixi/filters/VHSFilter'
import { VHSResFilter } from '@/pixi/filters/VHSResFilter'
import { OldCameraFilter } from '@/pixi/filters/OldCameraFilter'
import { Effect, SeratoFilter } from '@/pixi/filters/types'
import { EffectName } from '@/static/effectName'
import { DuotoneColours } from '@/static/colours'
import { EffectState } from '@/pixi/filters/types'
import useAudioEngine from '@/composables/UseAudioEngine'
import { useTransportEngine } from '@/composables/UseTransportEngine'

export class EffectsFactory {

  constructor (
    private transportEngine = useTransportEngine(),
    private audioEngine = useAudioEngine()
  ) { }
  /**
 * Creates an effect with the required filters and state
 * @param effectState 
 * @returns An Effect
 */
  public createEffect (effectState: EffectState): Effect {

    const applyFilterUniforms = (filter: SeratoFilter, timeStarted: number) => {
      filter.uniforms.audioFrequencyData = this.audioEngine.getAudioDataAtTime(this.transportEngine.getCurrentTransportTime())
      filter.uniforms.timeElapsed = this.transportEngine.getElapsedTime(timeStarted)
      filter.updateUniforms()
    }

    let primaryFilter: SeratoFilter | null = null

    // these filters get applied after the primary filter
    let postFilters: SeratoFilter[] = []
  
    switch (effectState.name) {
    case EffectName.CHROMA_SHIFT: {
      primaryFilter = new ChromaShiftFilter(effectState)
      break
    }
    case EffectName.RUBY_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.RUBY_DARK, DuotoneColours.RUBY_LITE)
      break
    }
    case EffectName.GRANITE_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.GRANITE_DARK, DuotoneColours.GRANITE_LITE)
      break
    }
    case EffectName.PEACH_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.PEACH_DARK, DuotoneColours.PEACH_LITE)
      break
    }
    case EffectName.DAMSON_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.DAMSON_DARK, DuotoneColours.DAMSON_LITE)
      break
    }
    case EffectName.GUAVA_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.GUAVA_DARK, DuotoneColours.GUAVA_LITE)
      break
    }
    case EffectName.GRAPE_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.GRAPE_DARK, DuotoneColours.GRAPE_LITE)
      break
    }
    case EffectName.LYCHEE_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.LYCHEE_DARK, DuotoneColours.LYCHEE_LITE)
      break
    }
    case EffectName.LIME_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.LIME_DARK, DuotoneColours.LIME_LITE)
      break
    }
    case EffectName.GRAPHITE_DUO: {
      primaryFilter = new DuotoneFilter(effectState, DuotoneColours.GRAPHITE_DARK, DuotoneColours.GRAPHITE_LITE)
      break
    }
    case EffectName.NEON: {
      primaryFilter = new NeonFilter(effectState)
      postFilters = [new BloomFilter(effectState)]
      break
    }
    case EffectName.NEON_BLEND: {
      primaryFilter = new NeonBlendFilter(effectState)
      break
    }
    case EffectName.PIXELATE: {
      primaryFilter = new PixelateFilter(effectState)
      break
    }
    case EffectName.RADIAL_BLUR: {
      primaryFilter = new RadialBlurFilter(effectState)
      break
    }
    case EffectName.ZOOM_BLUR: {
      primaryFilter = new ZoomBlurFilter(effectState)
      break
    }
    case EffectName.MOTION_BLUR: {
      primaryFilter = new MotionBlurFilter(effectState)
      break
    }
    case EffectName.OLD_TV: {
      primaryFilter = new OldTVFilter(effectState)
      break
    }
    case EffectName.FADED_FILM: {
      primaryFilter = new VintageFilter(effectState, fadedGradientStops)
      break
    }
    case EffectName.VINTAGE_FILM: {
      primaryFilter = new VintageFilter(effectState, vintageGradientStops)
      break
    }
    case EffectName.CONTRAST_FILM: {
      primaryFilter = new VintageFilter(effectState, contrastGradientStops)
      break
    }
    case EffectName.SEPIA_FILM: {
      primaryFilter = new VintageFilter(effectState, sepiaGradientStops)
      break
    }
    case EffectName.COOL_FILM: {
      primaryFilter = new VintageFilter(effectState, coolGradientStops)
      break
    }
    case EffectName.MIRROR_VERTICAL: {
      primaryFilter = new MirrorFilter(effectState, {
        numberOfMirrors: 1,
        isVerticalMirror: true
      })
      break
    }
    case EffectName.MIRROR_VERTICAL_FLIPPED: {
      primaryFilter = new MirrorFilter(effectState, {
        numberOfMirrors: 1,
        isVerticalMirror: true,
        isFlipped: true
      })
      break
    }
    case EffectName.MIRROR_HORIZONTAL: {
      primaryFilter = new MirrorFilter(effectState, {
        numberOfMirrors: 1,
        isVerticalMirror: false
      })
      break
    }
    case EffectName.MIRROR_HORIZONTAL_FLIPPED: {
      primaryFilter = new MirrorFilter(effectState, {
        numberOfMirrors: 1,
        isVerticalMirror: false,
        isFlipped: true
      })
      break
    }
    case EffectName.MIRROR_QUAD: {
      primaryFilter = new MirrorFilter(effectState, {
        numberOfMirrors: 2
      })
      break
    }
    case EffectName.KALEIDOSCOPE_QUAD: {
      primaryFilter = new KaleidoscopeFilter(effectState, {
        numberOfMirrors: 2
      })
      break
    }
    case EffectName.KALEIDOSCOPE_OCTO: {
      primaryFilter = new KaleidoscopeFilter(effectState, {
        numberOfMirrors: 4
      })
      break
    }
    case EffectName.VHS: {
      postFilters = [new VHSResFilter(effectState)]
      primaryFilter = new VHSFilter(effectState)
      break
    }
    case EffectName.OLD_CAMERA: {
      primaryFilter = new OldCameraFilter(effectState)
      break
    }
    case EffectName.DISPLACEMENT: {
      primaryFilter = new DisplacementFilter(effectState)
      break
    }
    }
    
    const timeStarted = this.transportEngine.getTimeUnixTimestamp()
    const activeFilters = primaryFilter ? [primaryFilter].concat(postFilters) : []
    const effect : Effect = {
      name: effectState.name,
      groupName: effectState.groupName,
      filters: activeFilters,
      updateFilterCallback: () => {
        effect.filters.forEach(filter => {
          applyFilterUniforms(filter, timeStarted)
        })
      }
    }
    return effect
  }
}
